var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import { styled, ListItem, ListItemButton, listItemButtonClasses, ListItemText, listItemTextClasses, ListItemIcon, Popper, Box, checkboxClasses, formControlLabelClasses, } from '@mui/material';
import { cloneElement, isValidElement } from '../../utils/reactNode';
import { ArrowDropRightIcon, DoneIcon } from '../icons';
import MenuListContext from '../MenuList/MenuListContext';
import Checkbox from '../Checkbox';
import Avatar from '../Avatar';
var MenuItemButton = styled(ListItemButton)(function (_a) {
    var _b;
    var ownerState = _a.ownerState;
    return (__assign(__assign({ paddingTop: 0, paddingBottom: 0 }, (ownerState.subMenuList && {
        paddingRight: 0,
    })), (_b = {}, _b["&." + listItemButtonClasses.selected] = {
        backgroundColor: 'transparent',
    }, _b)));
});
export var MenuItemText = styled(ListItemText)(function (_a) {
    var _b;
    var secondary = _a.secondary;
    return (__assign((_b = { marginTop: 7, marginBottom: 7 }, _b["& ." + listItemTextClasses.primary] = {
        fontSize: '14px',
        lineHeight: '22px',
    }, _b["& ." + listItemTextClasses.secondary] = {
        fontSize: '12px',
        lineHeight: '18px',
    }, _b), (secondary && ({
        marginTop: 4,
        marginBottom: 4,
    }))));
});
var MenuItemStartIcon = styled(ListItemIcon)(function (_a) {
    var _b;
    var ownerState = _a.ownerState;
    return __assign({ minWidth: 40 }, ((ownerState === null || ownerState === void 0 ? void 0 : ownerState.checkbox) && (_b = {
            minWidth: 'auto'
        },
        _b["& ." + checkboxClasses.root] = {
            '&:hover': {
                background: 'none',
            },
        },
        _b["& ." + formControlLabelClasses.root] = {
            marginRight: 0,
        },
        _b)));
});
var MenuItem = React.forwardRef(function (props, ref) {
    var disablePadding = props.disablePadding, disabled = props.disabled, text = props.text, secondaryText = props.secondaryText, _a = props.showAvatar, showAvatar = _a === void 0 ? false : _a, avatar = props.avatar, startIcon = props.startIcon, subMenuList = props.subMenuList, ListItemProps = props.ListItemProps, PopperProps = props.PopperProps, onMouseMove = props.onMouseMove, onMouseLeave = props.onMouseLeave, onClick = props.onClick, childrenProp = props.children, checkboxProp = props.checkbox, selectedProp = props.selected, multipleProp = props.multiple, value = props.value, moreProps = __rest(props, ["disablePadding", "disabled", "text", "secondaryText", "showAvatar", "avatar", "startIcon", "subMenuList", "ListItemProps", "PopperProps", "onMouseMove", "onMouseLeave", "onClick", "children", "checkbox", "selected", "multiple", "value"]);
    var menuListContext = React.useContext(MenuListContext) || {};
    var multiple = React.useMemo(function () {
        if (multipleProp !== undefined)
            return !!multipleProp;
        return !!menuListContext.multiple;
    }, [multipleProp, menuListContext.multiple]);
    var selected = React.useMemo(function () {
        if (selectedProp !== undefined)
            return selectedProp;
        var cValue = menuListContext === null || menuListContext === void 0 ? void 0 : menuListContext.value;
        if (cValue !== undefined && value !== undefined) {
            if (multiple) {
                return cValue === null || cValue === void 0 ? void 0 : cValue.includes(value);
            }
            return cValue === value;
        }
        return false;
    }, [selectedProp, value, menuListContext === null || menuListContext === void 0 ? void 0 : menuListContext.value]);
    var checkbox = childrenProp;
    if (multiple && checkbox === undefined) {
        checkbox = React.createElement(Checkbox, null);
    }
    var _b = React.useState(null), anchorEl = _b[0], setAnchorEl = _b[1];
    var _c = React.useState(false), open = _c[0], setOpen = _c[1];
    var handleMouseEnter = function (e) {
        setAnchorEl(e.currentTarget);
        setOpen(true);
        onMouseMove === null || onMouseMove === void 0 ? void 0 : onMouseMove(e);
    };
    var handleMouseLeave = function (e) {
        setOpen(false);
        onMouseLeave === null || onMouseLeave === void 0 ? void 0 : onMouseLeave(e);
    };
    var handleClick = function (e) {
        var _a;
        if (disabled)
            return;
        onClick === null || onClick === void 0 ? void 0 : onClick(e);
        (_a = menuListContext === null || menuListContext === void 0 ? void 0 : menuListContext.onChange) === null || _a === void 0 ? void 0 : _a.call(menuListContext, value);
    };
    var ownerState = {
        subMenuList: subMenuList,
        checkbox: checkbox,
    };
    var children = childrenProp;
    if (children == null) {
        children = (React.createElement(MenuItemText, { primary: text, secondary: secondaryText }));
    }
    return (React.createElement(ListItem, __assign({ onMouseMove: handleMouseEnter, onMouseLeave: handleMouseLeave, disablePadding: disablePadding }, ListItemProps),
        React.createElement(MenuItemButton, __assign({ disabled: disabled, dense: false, ownerState: ownerState, selected: selected }, moreProps, { onClick: handleClick }),
            startIcon && (React.createElement(MenuItemStartIcon, null, startIcon)),
            isValidElement(checkbox) && (React.createElement(MenuItemStartIcon, { ownerState: ownerState }, cloneElement(checkbox, {
                disabled: disabled,
                value: value,
                checked: selected,
            }))),
            showAvatar && (avatar || typeof text === 'string') && (React.createElement(Box, { sx: { display: 'flex', p: '6px', pl: 0 } },
                React.createElement(Avatar, { src: avatar }, text === null || text === void 0 ? void 0 : text.slice(-1)))),
            children,
            subMenuList != null && (React.createElement(ArrowDropRightIcon, null)),
            (multiple === false && selected === true) &&
                React.createElement(DoneIcon, { color: "primary" })),
        subMenuList && (React.createElement(Popper, __assign({ open: open, anchorEl: anchorEl, placement: 'right-start' }, PopperProps),
            React.createElement(Box, { sx: { marginLeft: '2px' } }, subMenuList)))));
});
MenuItem.defaultProps = {
    disablePadding: true
};
export default MenuItem;
